+<template>
  <div>
    <div class="container">
      <div class="row">
        <div class="col">
          <component
            :is="viewType"
            v-bind="viewBindings"
            :items="items"
            :loading="isLoading"
            :class="carouselItemTheme"
            :num-items="numItems"
          >
            <template slot-scope="{ item }">
              <component
                :is="componentFile"
                :item="item"
                :hover-card-enabled="hoverCardSetting"
              />
            </template>
          </component>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import Carousel from './displays/Carousel'
import List from './displays/List'

import loaders from './loaders.js'
import applogger from '@applogger'

export default {
  components: {
    Carousel,
    List
  },
  props: {
    widget: {
      type: Object,
      required: true
    },
    viewBindings: {
      type: Object,
      required: false,
      default: () => {}
    },
    filestore: {
      type: [Object, String],
      required: true
    }
  },
  data() {
    return {
      items: [],
      isLoading: false
    }
  },
  computed: {
    listType() {
      let listType = this.widget.widgetSettings.listType
      const listTypeParts = listType.split(('-'))
      if (listTypeParts.length === 2 && (listTypeParts[0] === 'latest' || listTypeParts[0] === 'featured')) {
        // Not using the updated blob structure. Caress the old structure.
        listType = listTypeParts[1]
      }
      return listType
    },
    componentType() {
      switch (this.listType) {
      case 'news':
        return 'news'
      case 'wines':
      case 'products':
      default:
        return 'products'
      }
    },
    componentFile() {
      return this.importComponent(this.componentType)
    },
    latest() {
      if (this.widget.widgetSettings.sortType) {
        return this.widget.widgetSettings.sortType === 'latest'
      }
      else {
        return this.widget.widgetSettings.listType.split('-').shift() === 'latest'
      }
    },
    featured() {
      if (this.widget.widgetSettings.isFeatured) {
        return this.widget.widgetSettings.isFeatured
      }
      else {
        return this.widget.widgetSettings.listType.split('-').shift() === 'featured'
      }
    },
    viewType() {
      return this.widget.widgetSettings.viewType
    },
    layout() {
      return this.widget.widgetSettings.layout ?? '0001'
    },
    maxItems() {
      return this.widget.widgetSettings.maxItems
    },
    numItems() {
      return this.widget.widgetSettings.numItems
    },
    sortType() {
      return this.widget.widgetSettings.sortType
    },
    selectedSeries() {
      return this.widget.widgetSettings.selectedSeries
    },
    selectedTypes() {
      return this.widget.widgetSettings.selectedTypes
    },
    selectedVarietals() {
      return this.widget.widgetSettings.selectedVarietals
    },
    articleCategory() {
      return this.widget.widgetSettings.articleCategory
    },
    carouselItemTheme() {
      return this.retrieveConcilioLayoutSetting('theme')
    },
    concilioSettings() {
      return Object.keys(this.themeSettings).includes('concilio-settings') ? this.themeSettings['concilio-settings'] : null
    },
    themeSettings() {
      return this.$store.state.registry.getModuleSetting('theme') || {}
    },
    themeProductSettings() {
      return Object.keys(this.themeSettings).includes('products') ? this.themeSettings.products : null
    },
    // themeConcilioSettings() {
    //   return Object.keys(this.themeSettings).includes('concilio-settings') ? this.themeSettings.concilioSettings : {}
    // },
    // carouselData() {
    //   return this.products.slice(0, this.maxItems)
    // },
    // carouselItemSettings() {
    //   return this.themeSettings !== null ? this.themeSettings.sliders : null
    // },
    hoverCardSetting() {
      return this.retrieveConcilioLayoutSetting('hoverCard')
    },
    carouselItemLayout() {
      const themeSettingsActive = (this.themeProductSettings !== null && Object.keys(this.themeProductSettings).includes('sliders'))
      return (themeSettingsActive) ? `${this.themeProductSettings.sliders}` || 'carousel-product-layout-0001' : 'carousel-product-layout-0001'
    }
  },
  watch: {
    maxItems() {
      this.getItems()
    },
    sortType() {
      this.getItems()
    },
    selectedSeries() {
      this.getItems()
    },
    selectedTypes() {
      this.getItems()
    },
    selectedVarietals() {
      this.getItems()
    },
    featured() {
      this.getItems()
    },
    articleCategory() {
      this.getItems()
    }
  },
  created() {
    this.getItems()
  },
  methods: {
    importComponent(type) {
      // Unable to use a string template here due to a bug in EsLint. Can be updated once issue is resolved.
      return () => import('./items/' + type + '/layouts/' + this.layout + '/' + this.viewType + '.vue')
    },
    getWineItems() {
      this.isLoading = true

      const includesAValue = (srcArray, filtersArray) => {
        let intersection
        if (Array.isArray(srcArray)) {
          intersection = srcArray.filter(v => filtersArray.includes(v))
        }
        else {
          intersection = filtersArray.filter(v => v === srcArray)
        }
        return intersection.length !== 0
      }

      // Filter and sort
      // getAdHocStorageValue('winecatalog') stores {id}-only references
      // (see winecatalog-preload-helper.js) - dereference against the main
      // catalog before filtering on real product fields. Untracked: this
      // pass touches every candidate in the catalogue just to filter/sort
      // it down, and the tracked getter would mark all of them as
      // SSR-accessed - defeating page-scoped pruning. Only the items that
      // survive filtering and the maxItems slice below are actually
      // rendered, so only those get tracked, further down.
      let items = this.$store.getters.getAdHocStorageValue('winecatalog')
        .map(ref => this.$store.getters['products/findProductByIDUntracked'](ref.id) || ref)
        .filter(item => {
          if (item.type !== 'wine_product') return false

          if (!item.allow_in_cart) return false

          if (this.featured && !item.featured) return false

          if (this.$store.getters['user/userid'] !== '1' && item.catalogvisibility && item.catalogvisibility.length !== 0 && !this.$store.getters['user/hasAnyRole'](item.catalogvisibility.map(data => data.role))) return false

          return [['series', 'Series'], ['wine_type', 'Types'], ['varietal', 'Varietals'], ['brand', 'Brand'], ['region', 'Region'], ['collections', 'Collections'], ['attributes', 'Attributes'], ['producer', 'Producer']].reduce((show, filter) => {
            if (this.widget.widgetSettings[`selected${filter[1]}`] && this.widget.widgetSettings[`selected${filter[1]}`].length) {
              return show && includesAValue(item[filter[0]], this.widget.widgetSettings[`selected${filter[1]}`])
            }
            else {
              return show
            }
          }, true)
        })

      if (this.widget.widgetSettings.sortType !== 'default') {
        let sortBy = 'dateChanged'
        let sortDir = -1 // -1 is DESC, 1 is ASC
        switch (this.widget.widgetSettings.sortType) {
        case 'latest':
          break
        case 'oldest':
          sortDir = 1
          break
        case 'expensive':
          sortBy = 'priceDiscounted'
          break
        case 'cheapest':
          sortBy = 'priceDiscounted'
          sortDir = 1
          break
        }
        items = items.sort((a, b) => {
          if ((a[sortBy] === null || b[sortBy] === null) || (typeof a[sortBy] !== typeof b[sortBy])) {
            return null
          }

          if (typeof a[sortBy] === 'string') {
            return sortDir * (a[sortBy]).localeCompare(b[sortBy], 'co', { numeric: true, usage: 'sort', sensitivity: 'base' })
          }
          else {
            if (a[sortBy] < b[sortBy]) return -1 * sortDir
            if (a[sortBy] > b[sortBy]) return sortDir
            return 0
          }
        })
      }

      const rendered = items.slice(0, this.maxItems)
      // Track only the items that actually survived filtering/sorting and
      // will be rendered - see the untracked lookup above.
      rendered.forEach(item => this.$store.getters['products/findProductByID'](item.id))
      this.$set(this, 'items', rendered)
      this.isLoading = false
    },
    getProductItems() {
      this.getGqlItems('loadProducts')
    },
    getNewsItems() {
      this.getGqlItems('loadNews', { articleCategory: this.articleCategory })
    },
    getGqlItems(loader, additionalOptions) {
      this.isLoading = true
      const items = this.$store.getters.getAdHocStorageValue(`content-list-${this.listType}-${this.maxItems}`)
      if (items) {
        this.$set(this, 'items', items)
        this.isLoading = false
      }
      else {
        loaders[loader]({
          state: this.$store.state,
          getters: this.$store.getters,
          commit: this.$store.commit,
          dispatch: this.$store.dispatch
        }, {
          filestore: this.filestore,
          key: this.listType,
          limit: this.maxItems,
          latest: this.latest,
          featured: this.featured,
          ...additionalOptions
        })
          .then(items => {
            this.$set(this, 'items', items)
          }).catch(err => {
            applogger.debug('Error Loading Content List - ' + err.message)
            const statusMessage = {
              type: 'error',
              title: 'Error loading the content list.',
              text: err.message,
              notification: true,
              showClose: true,
              timeout: 6000
            }
            this.$store.dispatch('reportError', statusMessage)
          }).finally(() => {
            this.isLoading = false
          })
      }
    },
    getItems() {
      switch (this.listType) {
      case 'wines':
        this.getWineItems()
        break

      case 'products':
        this.getProductItems()
        break

      case 'news':
        this.getNewsItems()
        break
      }
    },
    retrieveConcilioLayoutSetting(payload) {
      const concilioSettingsExist = this.concilioSettings !== null
      const currentLayout = this.carouselItemLayout
      const layoutSettingsExist = (concilioSettingsExist) ? Object.keys(this.concilioSettings).includes(currentLayout) : false

      return (concilioSettingsExist) ? layoutSettingsExist ? this.concilioSettings[currentLayout][payload] : null : null
    }

  }
}

</script>
